/**
 * Screen (+ optional microphone) recording for bug reports. The browser asks
 * which surface to share; Chrome offers this tab first (`preferCurrentTab`).
 * The result is one video file with the voice mixed in, ready to upload.
 */

export function canRecordScreen(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getDisplayMedia === 'function' &&
    typeof window.MediaRecorder === 'function'
  );
}

const MIME_CANDIDATES = [
  'video/webm;codecs=vp9,opus',
  'video/webm;codecs=vp8,opus',
  'video/webm',
  'video/mp4;codecs=avc1,mp4a.40.2',
  'video/mp4'
];

/** Recordings stop themselves here, which keeps uploads well under the limit. */
export const MAX_RECORDING_MS = 10 * 60 * 1000;

export interface Recording {
  blob: Blob;
  durationMs: number;
  hasAudio: boolean;
}

export interface ActiveRecording {
  startedAt: number;
  hasAudio: boolean;
  /** Resolves once recording has stopped, for whatever reason. */
  done: Promise<Recording>;
  stop: () => void;
  /** Stop and throw the footage away. */
  cancel: () => void;
}

export class RecordingError extends Error {}

export async function startRecording({ withAudio }: { withAudio: boolean }): Promise<ActiveRecording> {
  let display: MediaStream;
  try {
    display = await navigator.mediaDevices.getDisplayMedia({
      video: { frameRate: 15, displaySurface: 'browser' },
      audio: false,
      // Chrome-only hints; other browsers ignore them.
      preferCurrentTab: true,
      selfBrowserSurface: 'include',
      surfaceSwitching: 'exclude'
    } as DisplayMediaStreamOptions);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'NotAllowedError') {
      throw new RecordingError('Screen sharing was cancelled.');
    }
    throw new RecordingError('This browser could not start a screen recording.');
  }

  // Asked for after the screen, because getDisplayMedia must run inside the
  // click that started it and a microphone prompt in between would spend it.
  let mic: MediaStream | null = null;
  if (withAudio) {
    try {
      mic = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true }
      });
    } catch {
      mic = null;
    }
  }

  const stream = new MediaStream([...display.getVideoTracks(), ...(mic?.getAudioTracks() ?? [])]);
  const mimeType = MIME_CANDIDATES.find((type) => MediaRecorder.isTypeSupported(type));
  const recorder = new MediaRecorder(stream, {
    mimeType,
    videoBitsPerSecond: 1_500_000,
    audioBitsPerSecond: 64_000
  });

  const chunks: Blob[] = [];
  const startedAt = Date.now();
  let cancelled = false;

  const releaseDevices = () => {
    display.getTracks().forEach((track) => track.stop());
    mic?.getTracks().forEach((track) => track.stop());
  };

  const done = new Promise<Recording>((resolve, reject) => {
    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunks.push(e.data);
    };
    recorder.onstop = () => {
      releaseDevices();
      window.clearTimeout(limit);
      if (cancelled) return reject(new RecordingError('cancelled'));
      resolve({
        blob: new Blob(chunks, { type: (recorder.mimeType || mimeType || 'video/webm').split(';')[0] }),
        durationMs: Date.now() - startedAt,
        hasAudio: Boolean(mic)
      });
    };
  });

  const stop = () => {
    if (recorder.state !== 'inactive') recorder.stop();
  };
  // Chrome's own "Stop sharing" bar ends the video track; treat it as Stop.
  display.getVideoTracks()[0]?.addEventListener('ended', stop);
  const limit = window.setTimeout(stop, MAX_RECORDING_MS);

  recorder.start(1000);

  return {
    startedAt,
    hasAudio: Boolean(mic),
    done,
    stop,
    cancel: () => {
      cancelled = true;
      stop();
    }
  };
}
